import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import type { PlatformUserRow } from '@etiquetachef/types';
import { toast } from '@/lib/notifications';
import { adminSetContactVerificationCallable } from '@/lib/callables';

const PHONE_RE = /^\+\d{8,20}$/;

export type ContactVerificationTarget = {
  user: PlatformUserRow;
  channel: 'EMAIL' | 'PHONE';
  verified: boolean;
};

type Props = {
  target: ContactVerificationTarget | null;
  onClose: () => void;
  onDone: () => void;
};

/**
 * Carimba ou revoga a verificação de contato (issue #257).
 *
 * Duas coisas que o diálogo precisa deixar explícitas, porque nenhuma delas é
 * óbvia para quem clica:
 *
 * 1. **Desverificar derruba as sessões.** É o que faz a revogação valer agora
 *    em vez de em até 1h (tempo de vida do ID token).
 * 2. **O país do telefone é o da empresa** e não é escolhível. A empresa é
 *    selecionada entre as da própria pessoa, e o DDI aparece como prefixo
 *    fixo — a regra do projeto é que telefone de tenant é sempre do país que o
 *    OWNER fixou no OTP do onboarding.
 *
 * Origem: #257 — commit b3bc5df63, 2026-08-11 17:08 +0200.
 *
 * Contrato: `adminSetContactVerification({ targetUid, tenantId, channel,
 * verified, phoneE164? })` — `phoneE164` só ao verificar telefone. Devolve
 * `sessionsRevoked`, que escolhe o toast. Erro de regra mostra a mensagem do
 * servidor sem o prefixo (país do telefone, número duplicado, STAFF).
 *
 * Usado por: `routes/UsersPage.tsx`.
 *
 * Arquitetura: a validação do país é do callable
 * (`apps/functions/src/admin/adminSetContactVerification.ts`), contra o
 * `countryCode` da empresa escolhida.
 *
 * Falhas conhecidas (divergências entre o texto acima e o código, ver #926,
 * achados):
 *  - o item 2 diz que o DDI aparece como prefixo fixo, mas o campo de
 *    telefone é um `Input` livre iniciado com `+` — nenhum prefixo é travado
 *    na UI; só o servidor recusa outro país;
 *  - o país do tenant não vem mais do OTP do onboarding: desde a FD da #679
 *    (#744, D11) é fixado na Contratação do CRM.
 *
 * Issues: #257, #744.
 */
export function ContactVerificationDialog({
  target,
  onClose,
  onDone,
}: Props): React.JSX.Element | null {
  const { t } = useTranslation();
  const [tenantId, setTenantId] = useState<string>('');
  const [phone, setPhone] = useState('+');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const memberships = target?.user.memberships ?? [];

  useEffect(() => {
    if (!target) return;
    setTenantId(memberships[0]?.tenantId ?? '');
    setPhone(target.user.phoneE164 ?? '+');
    setError(null);
    // `target` troca a cada abertura; reinicializa o formulário.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  if (!target) return null;

  const { user, channel, verified } = target;
  const needsPhone = channel === 'PHONE' && verified;
  const canSubmit =
    tenantId.length > 0 && (!needsPhone || PHONE_RE.test(phone.trim()));

  async function handleSubmit(): Promise<void> {
    if (!target) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await adminSetContactVerificationCallable({
        targetUid: user.uid,
        tenantId,
        channel,
        verified,
        ...(needsPhone ? { phoneE164: phone.trim() } : {}),
      });
      toast.success(
        result.sessionsRevoked
          ? t('adminPlatformUsers.toast.verificationChangedAndRevoked')
          : t('adminPlatformUsers.toast.verificationChanged'),
      );
      onDone();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      // Erros de regra chegam com a mensagem do servidor (país do telefone,
      // número duplicado, STAFF). Mostrar a mensagem crua é melhor que um
      // genérico: ela diz exatamente o que impediu a operação.
      setError(message.replace(/^.*?:\s*/, ''));
    } finally {
      setSubmitting(false);
    }
  }

  const titleKey = `adminPlatformUsers.verifyDialog.title.${channel}_${
    verified ? 'ON' : 'OFF'
  }`;

  return (
    <Modal
      visible
      onDismiss={(): void => {
        if (!submitting) onClose();
      }}
      header={t(titleKey, { name: user.displayName || user.email })}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={submitting}>
              {t('common.cancel', { defaultValue: 'Cancelar' })}
            </Button>
            <Button
              variant="primary"
              loading={submitting}
              disabled={!canSubmit}
              onClick={(): void => {
                void handleSubmit();
              }}
            >
              {t('adminPlatformUsers.verifyDialog.confirm')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {error ? <Alert type="error">{error}</Alert> : null}

        {!verified ? (
          <Alert type="warning">{t('adminPlatformUsers.verifyDialog.revokeWarning')}</Alert>
        ) : null}

        <FormField
          label={t('adminPlatformUsers.verifyDialog.tenant')}
          description={t('adminPlatformUsers.verifyDialog.tenantHint')}
        >
          <Select
            selectedOption={
              memberships
                .filter((m) => m.tenantId === tenantId)
                .map((m) => ({ value: m.tenantId, label: m.tenantName }))[0] ?? null
            }
            options={memberships.map((m) => ({ value: m.tenantId, label: m.tenantName }))}
            onChange={({ detail }): void => {
              setTenantId(detail.selectedOption.value ?? '');
            }}
            disabled={submitting || memberships.length <= 1}
          />
        </FormField>

        {needsPhone ? (
          <FormField
            label={t('adminPlatformUsers.verifyDialog.phone')}
            description={t('adminPlatformUsers.verifyDialog.phoneHint')}
            errorText={
              phone.length > 1 && !PHONE_RE.test(phone.trim())
                ? t('adminPlatformUsers.verifyDialog.phoneInvalid')
                : undefined
            }
          >
            <Input
              value={phone}
              inputMode="tel"
              disabled={submitting}
              onChange={({ detail }): void => {
                setPhone(detail.value);
              }}
            />
          </FormField>
        ) : null}
      </SpaceBetween>
    </Modal>
  );
}
